// This is based on apollo-boost
// https://github.com/apollographql/apollo-client/blob/master/packages/apollo-boost/src/index.ts

import { ApolloClient, ApolloLink } from "@apollo/client";
import { InMemoryCache } from "@apollo/client/cache";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { onError } from "@apollo/client/link/error";
import { createHttpLink } from "@apollo/client/link/http";
import { print } from "graphql";
import fetch from "isomorphic-fetch";

import introspectionQueryResultData from "../generated/introspectionQueryResultData";
import { apiInternalPath } from "../generated/routes";

import { kibelaContext } from "./kibelaContext";

const ENDPOINT = apiInternalPath();
const possibleTypes = introspectionQueryResultData.possibleTypes;

const requestHeaders = {
  "X-Requested-With": "apollo",
};

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ message }) => console.error(`[GraphQL error]: `, message, print(operation.query)));
  }
  if (networkError) {
    console.error(`[Network error]: `, networkError, print(operation.query));
  }
});

const httpLinkOption = {
  uri: ENDPOINT,
  headers: requestHeaders,
  credentials: "same-origin",
  fetch,
  fetchOptions: {
    referrerPolicy: "unsafe-url",
  },
};

const batchHttpLink = new BatchHttpLink(httpLinkOption);

const httpLink = createHttpLink(httpLinkOption);

const updateCacheKeysInKibelaContext = (keyName: "foldersCacheKey" | "customEmojisCacheKey", data: any) => {
  const update = (newCacheKey: string | undefined) => {
    if (newCacheKey) {
      const before = kibelaContext[keyName];
      kibelaContext[keyName] = newCacheKey;
      console.log(`kibelaContext.${keyName} is updated from ${before} to ${newCacheKey}`);
    }
  };

  // For query.keyName
  update(data[keyName]);

  // For ANY_MUTATION.keyName
  for (const key in data) {
    if (data[key]) {
      update(data[key][keyName]);
    }
  }
};

const refreshCacheKeysLink = new ApolloLink((operation, forward) => {
  const observable = forward(operation);
  return observable.map((response) => {
    const data = response.data;
    if (data) {
      updateCacheKeysInKibelaContext("foldersCacheKey", data);
      updateCacheKeysInKibelaContext("customEmojisCacheKey", data);
    }

    return response;
  });
});

export const client = new ApolloClient({
  cache: new InMemoryCache({ possibleTypes }),
  link: refreshCacheKeysLink.concat(ApolloLink.from([errorLink, batchHttpLink])),
});

export const clientWithoutBatch = new ApolloClient({
  cache: new InMemoryCache({ possibleTypes }),
  link: refreshCacheKeysLink.concat(ApolloLink.from([errorLink, httpLink])),
});

export type KibelaClient2 = typeof client;
